import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import { ApiError, apiFetch } from '@/api/client';
import {
  getSupplierJournalEntries,
  getSupplierJournalEntriesById,
  getSupplierView,
} from '@/api/findings';
import {
  updateSupplierClassification,
  type SupplierClassificationUpdateIn,
} from '@/api/suppliers';
import type { FindingOut, JournalEntryOut, SupplierViewOut } from '@/api/types';
import { AttachmentChip } from '@/components/AttachmentChip';
import { FindingDispositionPanel } from '@/components/FindingDispositionPanel';
import { FindingTags } from '@/components/FindingTags';
import { SupplierBnTimelineSection } from '@/components/SupplierBnTimelineSection';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { StatusBadge } from '@/components/ui/status-badge';
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@/components/ui/table';
import { useTransactionDocuments } from '@/hooks/useTransactionDocuments';
import { tagsFor } from '@/lib/finding-tags';
import { coerceCurrency, formatCurrency, formatDateTime } from '@/lib/format';
import { stopRowActivation } from '@/lib/rowLink';
import {
  PROVINCE_OPTIONS,
  REGULATORY_CLASS_OPTIONS,
  TAX_CLASS_OPTIONS,
} from '@/lib/supplierClassificationOptions';
import { cycleSortState, sortRows, type SortState } from '@/lib/supplierTableSort';

import { useAuth } from '../auth/AuthContext';

/** Full-screen overlay that surfaces everything we know about one
 *  supplier on one run — classification + aggregates + every finding +
 *  raw GL lines (NetSuite-source only). Opened by clicking the
 *  supplier name on a finding card.
 *
 *  Classification (tax/regulatory class + province) and the BN timeline
 *  are editable in place; bulk actions live on the per-card supplier row.
 *
 *  Layout: large centered panel with inset padding from the viewport
 *  edges so 40 findings + 101 journal-entry rows have room to breathe.
 *  ESC, backdrop click, and the explicit "Close" / X buttons all
 *  dismiss. */
export function SupplierDrawer({
  runId,
  supplierName,
  onClose,
}: {
  runId: string;
  supplierName: string;
  onClose: () => void;
}) {
  const { userKey, getIdToken } = useAuth();

  const query = useQuery({
    queryKey: ['supplier-view', runId, supplierName, userKey],
    // Skip the journal-entry scan here; the JE section loads them on its own
    // so the drawer shell paints without waiting for the largest set.
    queryFn: async () =>
      getSupplierView(runId, supplierName, (await getIdToken()) ?? '', false),
  });

  // ESC closes. Attached to window so focus elsewhere doesn't swallow.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 lg:p-8">
      {/* Backdrop: click closes. Full viewport; the inner panel uses
          stopPropagation so clicks inside the content don't bubble. */}
      <button
        type="button"
        aria-label="Close supplier view"
        onClick={onClose}
        className="absolute inset-0 bg-brand-navy/50 backdrop-blur-sm"
      />
      {/* Panel: takes most of the viewport so the dense tables breathe.
          Caps at 7xl to keep the layout readable on ultrawide displays. */}
      <div
        className="relative flex h-full w-full max-w-7xl flex-col overflow-hidden rounded-lg border border-border bg-card text-card-foreground shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={`Supplier details for ${supplierName}`}
        aria-modal="true"
      >
        <header className="flex items-baseline justify-between gap-4 border-b border-border bg-card/95 px-6 py-4 backdrop-blur">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-wide text-brand-slate">Supplier</p>
            <h2 className="truncate text-2xl font-semibold tracking-tight text-brand-navy">
              {supplierName}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <p className="hidden text-xs text-brand-slate sm:block">
              <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px] text-brand-navy">
                Esc
              </kbd>{' '}
              to close
            </p>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={onClose}
              aria-label="Close"
              className="px-2"
            >
              <span aria-hidden className="text-xl leading-none">×</span>
            </Button>
          </div>
        </header>

        {/* flex-1 min-h-0 is what makes overflow-y-auto actually engage:
            flex items default to min-height:auto, so without it this div
            grows to content height and the panel's overflow-hidden clips
            the tail instead of scrolling. */}
        <div className="grid min-h-0 flex-1 content-start gap-6 overflow-y-auto px-6 py-5">
          {query.isPending ? (
            <Spinner label="Loading supplier details…" />
          ) : query.isError ? (
            <DrawerErrorState error={query.error} />
          ) : (
            <DrawerBody data={query.data} runId={runId} supplierName={supplierName} />
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function DrawerErrorState({ error }: { error: unknown }) {
  const isNotFound = error instanceof ApiError && error.status === 404;
  return (
    <div className="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3">
      <p className="text-sm font-medium text-destructive">
        {isNotFound ? 'No findings for this supplier on this run.' : 'Failed to load supplier details.'}
      </p>
      {!isNotFound && (
        <p className="mt-1 text-xs text-brand-slate">
          {error instanceof Error ? error.message : 'Unknown error.'}
        </p>
      )}
    </div>
  );
}

/** Same body the modal `SupplierDrawer` renders, but exported so the
 *  standalone `/runs/$id/suppliers/$supplierName` page can reuse it
 *  without re-implementing the supplier view from scratch. */
export function SupplierViewBody({
  data,
  runId,
  supplierName,
  customerSlug = null,
}: {
  data: SupplierViewOut;
  runId: string;
  supplierName: string;
  /** Enables the 📎 invoice chips on the findings table. Callers
   *  without customer context (CLI-triggered runs) omit it. */
  customerSlug?: string | null;
}) {
  return (
    <>
      {/* Top row: Aggregates spans most of the width; Classification rides
          alongside on wide screens. The tables underneath get full width. */}
      <div className="grid gap-6 lg:grid-cols-[1fr_minmax(280px,360px)]">
        <AggregatesSection data={data} />
        <ClassificationSection data={data} />
      </div>
      {data.supplier_id !== null && <SupplierBnTimelineSection supplierId={data.supplier_id} />}
      <FindingsSection data={data} customerSlug={customerSlug} runId={runId} />
      <JournalEntriesSection
        runId={runId}
        supplierId={data.supplier_id}
        supplierName={supplierName}
      />
      <FooterNote />
    </>
  );
}

/** Error pane shown by both the modal drawer and the standalone page. */
export function SupplierViewError({ error }: { error: unknown }) {
  return <DrawerErrorState error={error} />;
}

function DrawerBody({
  data,
  runId,
  supplierName,
}: {
  data: SupplierViewOut;
  runId: string;
  supplierName: string;
}) {
  return <SupplierViewBody data={data} runId={runId} supplierName={supplierName} />;
}

function AggregatesSection({ data }: { data: SupplierViewOut }) {
  const { aggregates } = data;
  return (
    <section className="grid gap-3">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-brand-slate">
        Overview
      </h3>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <BigStat
          label="Findings"
          value={String(aggregates.total_findings)}
          subtext={`${aggregates.by_status.approved ?? 0} approved · ${aggregates.by_status.pending ?? 0} pending`}
        />
        <BigStat
          label="Recovery (approved)"
          value={formatCurrency(aggregates.total_recovery_approved)}
          subtext="refundable"
          valueClassName="text-emerald-700"
        />
        <BigStat
          label="Compliance risk (approved)"
          value={formatCurrency(aggregates.total_compliance_risk_approved)}
          subtext="exposure"
          valueClassName="text-amber-700"
        />
        <BigStat
          label="Eff. rate"
          value={aggregates.effective_rate_pct !== null ? `${aggregates.effective_rate_pct}%` : '—'}
          subtext="paid / billed"
        />
      </div>
      {aggregates.by_rule.length > 0 && (
        <div className="grid gap-1.5 rounded-md border border-border bg-muted/30 px-3 py-2">
          <p className="text-xs font-medium uppercase tracking-wide text-brand-slate">
            By rule
          </p>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-brand-slate">
                <th className="font-normal">Rule</th>
                <th className="text-right font-normal">Count</th>
                <th className="text-right font-normal">Recovery</th>
              </tr>
            </thead>
            <tbody>
              {aggregates.by_rule.map((r) => (
                <tr key={r.rule_id} className="border-t border-border/40">
                  <td className="py-1 font-mono text-brand-navy">{r.rule_id}</td>
                  <td className="py-1 text-right tabular-nums text-brand-navy">{r.count}</td>
                  <td className="py-1 text-right font-mono tabular-nums text-brand-navy">
                    {formatCurrency(r.recovery)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function ClassificationSection({ data }: { data: SupplierViewOut }) {
  const { classification: c } = data;
  const supplierId = data.supplier_id;
  const { getIdToken } = useAuth();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [taxClass, setTaxClass] = useState<SupplierClassificationUpdateIn['tax_class']>(
    c.tax_class as SupplierClassificationUpdateIn['tax_class'],
  );
  const [regulatoryClass, setRegulatoryClass] = useState<
    SupplierClassificationUpdateIn['regulatory_class']
  >(c.regulatory_class as SupplierClassificationUpdateIn['regulatory_class']);
  const [province, setProvince] = useState(c.province ?? '');

  const save = useMutation({
    mutationFn: async () =>
      updateSupplierClassification(
        supplierId!,
        {
          tax_class: taxClass,
          regulatory_class: regulatoryClass,
          // The select only offers valid codes (or '' → null), so this
          // narrows safely to the province enum.
          province: (province || null) as SupplierClassificationUpdateIn['province'],
        },
        (await getIdToken()) ?? '',
      ),
    onSuccess: () => {
      setEditing(false);
      // Refetch the supplier view (drawer or standalone) so the edited
      // values + the `practitioner` source badge appear.
      void qc.invalidateQueries({ queryKey: ['supplier-view'] });
    },
  });

  const startEdit = () => {
    setTaxClass(c.tax_class as SupplierClassificationUpdateIn['tax_class']);
    setRegulatoryClass(c.regulatory_class as SupplierClassificationUpdateIn['regulatory_class']);
    setProvince(c.province ?? '');
    save.reset();
    setEditing(true);
  };

  return (
    <section className="grid gap-3">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-brand-slate">
          Classification
        </h3>
        {/* Editing writes a practitioner override to the DB supplier row; it
            needs a resolved supplier_id (absent on legacy / non-DB runs). */}
        {supplierId !== null && !editing && (
          <Button type="button" size="sm" variant="ghost" className="h-6 text-xs" onClick={startEdit}>
            Edit
          </Button>
        )}
      </div>
      <dl className="grid grid-cols-[max-content_minmax(0,1fr)] items-center gap-x-6 gap-y-1.5 rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
        <dt className="text-brand-slate">Tax class</dt>
        <dd className="text-brand-navy">
          {editing ? (
            <ClassificationSelect
              ariaLabel="Tax class"
              value={taxClass}
              onChange={(v) => setTaxClass(v as SupplierClassificationUpdateIn['tax_class'])}
              options={TAX_CLASS_OPTIONS}
            />
          ) : (
            <span className="font-mono">{c.tax_class}</span>
          )}
        </dd>
        <dt className="text-brand-slate">Regulatory class</dt>
        <dd className="text-brand-navy">
          {editing ? (
            <ClassificationSelect
              ariaLabel="Regulatory class"
              value={regulatoryClass}
              onChange={(v) =>
                setRegulatoryClass(v as SupplierClassificationUpdateIn['regulatory_class'])
              }
              options={REGULATORY_CLASS_OPTIONS}
            />
          ) : (
            <span className="font-mono">{c.regulatory_class}</span>
          )}
        </dd>
        <dt className="text-brand-slate">Province</dt>
        <dd className="text-brand-navy">
          {editing ? (
            <select
              aria-label="Province"
              value={province}
              onChange={(e) => setProvince(e.target.value)}
              className="w-full rounded border border-border bg-background px-2 py-1 font-mono text-xs"
            >
              <option value="">—</option>
              {PROVINCE_OPTIONS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          ) : (
            <span className="font-mono">{c.province ?? '—'}</span>
          )}
        </dd>
        <dt className="text-brand-slate">BN</dt>
        <dd className="font-mono text-brand-navy">{c.bn ?? '—'}</dd>
        <dt className="text-brand-slate">Source</dt>
        <dd>
          <ClassificationSourceBadge source={c.source} />
        </dd>
      </dl>
      {editing && (
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            className="text-xs"
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="text-xs"
            disabled={save.isPending}
            onClick={() => {
              save.reset();
              setEditing(false);
            }}
          >
            Cancel
          </Button>
          {save.isError && (
            <span className="text-xs text-destructive">Couldn&rsquo;t save — try again.</span>
          )}
        </div>
      )}
    </section>
  );
}

function ClassificationSelect({
  value,
  onChange,
  options,
  ariaLabel,
}: {
  value: string;
  onChange: (value: string) => void;
  options: readonly { value: string; label: string }[];
  ariaLabel: string;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label={ariaLabel}
      className="w-full rounded border border-border bg-background px-2 py-1 text-xs text-brand-navy"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

function ClassificationSourceBadge({ source }: { source: SupplierViewOut['classification']['source'] }) {
  if (source === 'practitioner') {
    return (
      <Badge className="border-brand-royal/40 bg-brand-royal/10 text-brand-navy hover:bg-brand-royal/10">
        practitioner
      </Badge>
    );
  }
  if (source === 'csv') {
    return (
      <Badge className="border-emerald-500/40 bg-emerald-50 text-emerald-900 hover:bg-emerald-50">
        curated CSV
      </Badge>
    );
  }
  if (source === 'simplified_registry_overlay') {
    return (
      <Badge className="border-sky-500/40 bg-sky-50 text-sky-800 hover:bg-sky-50">
        CRA simplified registry
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="border-amber-500/50 text-amber-900">
      no match — default
    </Badge>
  );
}

/** A clickable table header that cycles the column's sort on click and shows
 *  the active direction. Shared by the findings + journal-entry tables. */
function SortableTh<K extends string>({
  label,
  sortKey,
  sort,
  onSort,
  align = 'left',
}: {
  label: string;
  sortKey: K;
  sort: SortState<K> | null;
  onSort: (key: K) => void;
  align?: 'left' | 'right';
}) {
  const active = sort?.key === sortKey;
  const caret = active ? (sort.dir === 'asc' ? '▲' : '▼') : '';
  return (
    <th
      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      className={`px-2 py-1.5 font-medium ${align === 'right' ? 'text-right' : 'text-left'}`}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        aria-label={`Sort by ${label}`}
        className={`inline-flex items-center gap-1 uppercase tracking-wide hover:text-brand-navy ${
          active ? 'text-brand-navy' : ''
        }`}
      >
        {label}
        <span aria-hidden className="text-[8px] leading-none">
          {caret}
        </span>
      </button>
    </th>
  );
}

type FindingSortKey =
  | 'rule'
  | 'tx'
  | 'date'
  | 'status'
  | 'tags'
  | 'rate'
  | 'recovery'
  | 'reviewed';

function findingBillDate(f: FindingOut): string {
  const v = f.evidence['bill_date'];
  return typeof v === 'string' ? v : '';
}

/** Effective tax rate for a finding — tax ÷ bill from evidence, matching the
 *  engine's `implied_tax_rate` convention. Null when either amount is missing
 *  or the bill is non-positive. */
function findingEffectiveRate(f: FindingOut): number | null {
  const bill = Number(f.evidence['bill_amount']);
  const tax = Number(f.evidence['tax_amount']);
  if (!Number.isFinite(bill) || !Number.isFinite(tax) || bill <= 0 || tax <= 0) return null;
  return tax / bill;
}

function formatEffectiveRate(rate: number | null): string {
  if (rate == null) return '—';
  // Two decimals to match the finding-detail page's effective-rate display.
  return `${(rate * 100).toFixed(2)}%`;
}

function findingSortValue(f: FindingOut, key: FindingSortKey): string | number | null {
  switch (key) {
    case 'rule':
      return f.rule_id;
    case 'tx':
      return f.transaction_id;
    case 'date':
      return findingBillDate(f);
    case 'status':
      return f.status;
    case 'tags':
      // Sort by the same labels the pills render, so findings with the same
      // tags group together; untagged rows sort to one end.
      return tagsFor(f)
        .map((t) => t.label)
        .join(', ');
    case 'rate':
      // null sinks missing rates to the bottom in both sort directions.
      return findingEffectiveRate(f);
    case 'recovery':
      return Number(f.recovery.amount);
    case 'reviewed':
      return f.reviewed_at ?? '';
  }
}

function FindingsSection({
  data,
  customerSlug,
  runId,
}: {
  data: SupplierViewOut;
  customerSlug: string | null;
  runId: string;
}) {
  const { isSignedIn, userKey, getIdToken } = useAuth();
  const qc = useQueryClient();
  const transactionIds = useMemo(
    () => data.findings.map((f) => f.transaction_id),
    [data.findings],
  );
  const { linksByTransaction, extractionsByDocument } = useTransactionDocuments(
    customerSlug,
    transactionIds,
  );
  const navigate = useNavigate();
  const [sort, setSort] = useState<SortState<FindingSortKey> | null>(null);
  const onSort = (key: FindingSortKey) => setSort((s) => cycleSortState(s, key));
  // Per-row approve/reject/needs-review, mirroring the finding-detail flow
  // so a whole vendor's findings can be dispositioned without leaving the
  // page (INP-371). Practitioners only.
  const me = useQuery({
    queryKey: ['me', userKey],
    enabled: isSignedIn,
    queryFn: async () =>
      apiFetch<{ user_type: 'practitioner' | 'customer' }>('/api/me', {
        idToken: (await getIdToken()) ?? '',
      }),
  });
  const canReview = me.data?.user_type === 'practitioner';
  const [reviewingId, setReviewingId] = useState<string | null>(null);
  const refreshSupplierView = () => qc.invalidateQueries({ queryKey: ['supplier-view'] });
  const rows = useMemo(
    () => sortRows(data.findings, sort, findingSortValue),
    [data.findings, sort],
  );
  return (
    <section className="grid gap-3">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-brand-slate">
        Findings ({data.findings.length})
      </h3>
      {rows.length === 0 ? (
        <p className="text-xs text-brand-slate">No findings for this supplier on this run.</p>
      ) : (
      <Table>
        <TableHeader className="uppercase tracking-wide text-brand-slate">
          <TableRow>
            <SortableTh label="Rule" sortKey="rule" sort={sort} onSort={onSort} />
            <SortableTh label="Tx" sortKey="tx" sort={sort} onSort={onSort} />
            <SortableTh label="Date" sortKey="date" sort={sort} onSort={onSort} />
            <SortableTh label="Status" sortKey="status" sort={sort} onSort={onSort} />
            <SortableTh label="Tags" sortKey="tags" sort={sort} onSort={onSort} />
            <SortableTh
              label="Eff. rate"
              sortKey="rate"
              sort={sort}
              onSort={onSort}
              align="right"
            />
            <SortableTh
              label="Recovery"
              sortKey="recovery"
              sort={sort}
              onSort={onSort}
              align="right"
            />
            <SortableTh label="Reviewed" sortKey="reviewed" sort={sort} onSort={onSort} />
            {canReview && (
              <th className="px-2 py-1.5 text-left font-medium">Review</th>
            )}
          </TableRow>
        </TableHeader>
        <TableBody className="[&_td]:text-xs">
          {rows.map((f) => (
            <Fragment key={f.finding_id}>
            <TableRow
              onClick={() =>
                void navigate({
                  to: '/runs/$id/findings/$findingId',
                  params: { id: f.audit_run_id, findingId: f.finding_id },
                })
              }
            >
              <TableCell className="whitespace-nowrap px-2 py-1.5 font-mono text-brand-navy">
                <Link
                  to="/runs/$id/findings/$findingId"
                  params={{ id: f.audit_run_id, findingId: f.finding_id }}
                  onClick={stopRowActivation}
                  className="underline-offset-4 hover:underline"
                  aria-label={`Open detail for ${f.rule_id} finding on transaction ${f.transaction_id}`}
                >
                  {f.rule_id}
                </Link>
              </TableCell>
              <TableCell className="whitespace-nowrap px-2 py-1.5 font-mono text-brand-slate">
                <span className="flex items-center gap-1.5">
                  {f.transaction_id}
                  <span onClick={stopRowActivation}>
                    <AttachmentChip
                      links={linksByTransaction.get(f.transaction_id)}
                      extractions={extractionsByDocument}
                      customerSlug={null}
                      compact
                    />
                  </span>
                </span>
              </TableCell>
              <TableCell className="whitespace-nowrap px-2 py-1.5 text-brand-slate">
                {findingBillDate(f) || '—'}
              </TableCell>
              <TableCell className="whitespace-nowrap px-2 py-1.5">
                <StatusBadge status={f.status} />
              </TableCell>
              <TableCell className="whitespace-nowrap px-2 py-1.5">
                <FindingTags finding={f} />
              </TableCell>
              <TableCell className="whitespace-nowrap px-2 py-1.5 text-right font-mono tabular-nums text-brand-slate">
                {formatEffectiveRate(findingEffectiveRate(f))}
              </TableCell>
              <TableCell className="whitespace-nowrap px-2 py-1.5 text-right font-mono tabular-nums text-brand-navy">
                {formatCurrency(f.recovery.amount, coerceCurrency(f.recovery.currency))}
              </TableCell>
              <TableCell className="whitespace-nowrap px-2 py-1.5 text-brand-slate">
                {f.reviewed_at ? formatDateTime(f.reviewed_at) : '—'}
              </TableCell>
              {canReview && (
                <TableCell className="whitespace-nowrap px-2 py-1.5">
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-6 px-2 text-xs"
                    aria-expanded={reviewingId === f.finding_id}
                    onClick={(e) => {
                      e.stopPropagation();
                      setReviewingId((id) => (id === f.finding_id ? null : f.finding_id));
                    }}
                  >
                    Review
                  </Button>
                </TableCell>
              )}
            </TableRow>
            {canReview && reviewingId === f.finding_id && (
              <TableRow>
                <TableCell colSpan={9} className="px-2 py-2">
                  <FindingDispositionPanel
                    key={f.finding_id}
                    finding={f}
                    runId={runId}
                    canReview={canReview}
                    onDispositioned={refreshSupplierView}
                  />
                </TableCell>
              </TableRow>
            )}
            </Fragment>
          ))}
        </TableBody>
      </Table>
      )}
    </section>
  );
}

type JournalSortKey = 'date' | 'tx' | 'gl' | 'bill' | 'tax';

function journalSortValue(e: JournalEntryOut, key: JournalSortKey): string | number {
  switch (key) {
    case 'date':
      return e.bill_date ?? '';
    case 'tx':
      return e.transaction_id;
    case 'gl':
      return e.gl_account_name;
    case 'bill':
      return Number(e.bill.amount);
    case 'tax':
      return Number(e.tax.amount);
  }
}

function JournalEntriesSection({
  runId,
  supplierId,
  supplierName,
}: {
  runId: string;
  /** Prefer the id-keyed endpoint (safe against a `/` in the name) when
   *  the supplier has a `suppliers` row; fall back to the name otherwise. */
  supplierId: string | null;
  supplierName: string;
}) {
  const { userKey, getIdToken } = useAuth();
  const [sort, setSort] = useState<SortState<JournalSortKey> | null>(null);
  const onSort = (key: JournalSortKey) => setSort((s) => cycleSortState(s, key));
  // Loaded on its own (not bundled in the supplier view) so the page shell
  // renders first (INP-517).
  const query = useQuery({
    queryKey: [
      'supplier-journal-entries',
      runId,
      supplierId ? `id:${supplierId}` : `name:${supplierName}`,
      userKey,
    ],
    queryFn: async () => {
      const token = (await getIdToken()) ?? '';
      return supplierId
        ? getSupplierJournalEntriesById(runId, supplierId, token)
        : getSupplierJournalEntries(runId, supplierName, token);
    },
  });
  const entries = query.data?.entries ?? null;
  const rows = useMemo(() => sortRows(entries ?? [], sort, journalSortValue), [entries, sort]);

  const heading = (
    <h3 className="text-xs font-semibold uppercase tracking-wide text-brand-slate">
      Journal entries{entries && entries.length > 0 ? ` (${entries.length})` : ''}
    </h3>
  );

  if (query.isPending) {
    return (
      <section className="grid gap-2">
        {heading}
        <Spinner label="Loading journal entries…" />
      </section>
    );
  }
  if (query.isError) {
    return (
      <section className="grid gap-2">
        {heading}
        <p className="text-xs text-destructive">Failed to load journal entries.</p>
      </section>
    );
  }
  if (entries === null) {
    return (
      <section className="grid gap-2">
        {heading}
        <p className="text-xs text-brand-slate">
          Not available — this run was not NetSuite-source.
        </p>
      </section>
    );
  }
  if (entries.length === 0) {
    return (
      <section className="grid gap-2">
        {heading}
        <p className="text-xs text-brand-slate">
          No journal entries found for this supplier on this run.
        </p>
      </section>
    );
  }
  return (
    <section className="grid gap-3">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-brand-slate">
        Journal entries ({entries.length})
      </h3>
      <Table>
        <TableHeader className="uppercase tracking-wide text-brand-slate">
          <TableRow>
            <SortableTh label="Date" sortKey="date" sort={sort} onSort={onSort} />
            <SortableTh label="Tx" sortKey="tx" sort={sort} onSort={onSort} />
            <SortableTh label="GL account" sortKey="gl" sort={sort} onSort={onSort} />
            <SortableTh label="Bill amt" sortKey="bill" sort={sort} onSort={onSort} align="right" />
            <SortableTh label="Tax amt" sortKey="tax" sort={sort} onSort={onSort} align="right" />
          </TableRow>
        </TableHeader>
        <TableBody className="[&_td]:text-xs">
          {rows.map((e) => (
            <TableRow key={e.transaction_id}>
              <TableCell className="whitespace-nowrap px-2 py-1.5 text-brand-slate">
                {e.bill_date ?? '—'}
              </TableCell>
              <TableCell className="whitespace-nowrap px-2 py-1.5 font-mono text-brand-slate">
                {e.transaction_id}
              </TableCell>
              <TableCell className="px-2 py-1.5 text-brand-navy">{e.gl_account_name}</TableCell>
              <TableCell className="whitespace-nowrap px-2 py-1.5 text-right font-mono tabular-nums text-brand-navy">
                {formatCurrency(e.bill.amount, coerceCurrency(e.bill.currency))}
              </TableCell>
              <TableCell className="whitespace-nowrap px-2 py-1.5 text-right font-mono tabular-nums text-brand-navy">
                {formatCurrency(e.tax.amount, coerceCurrency(e.tax.currency))}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </section>
  );
}

function FooterNote() {
  return (
    <p className="border-t border-border/60 pt-3 text-xs text-brand-slate">
      Showing flagged findings only. The denominator (total transactions for this supplier)
      becomes available when{' '}
      <a
        href="https://linear.app/input-recovery/issue/INP-219/slice-4-persist-source-files-in-s3-replace-per-run-tempfiles"
        target="_blank"
        rel="noopener noreferrer"
        className="text-brand-royal underline-offset-4 hover:underline"
      >
        INP-219
      </a>{' '}
      ships S3 source-file persistence.
    </p>
  );
}

function BigStat({
  label,
  value,
  subtext,
  valueClassName,
}: {
  label: string;
  value: string;
  subtext: string;
  valueClassName?: string;
}) {
  return (
    <div className="rounded-md border border-border bg-background px-3 py-2">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-brand-slate">
        {label}
      </p>
      <p
        className={`mt-0.5 text-base font-semibold tabular-nums ${valueClassName ?? 'text-brand-navy'}`}
      >
        {value}
      </p>
      <p className="text-[10px] text-brand-slate">{subtext}</p>
    </div>
  );
}
